import React, { useEffect, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import { registrationService, getErrorMessage, getErrorCode, getErrorDetails } from '../../../services/apiService';
import { gstinProblem, normalizeGstin, panFromGstin } from '../../../utils/gstin';
import { preferTradeName, distinctLegalName } from '../../../utils/vendorName';
import ClearInputButton from './ClearInputButton';
import { useSlowFlag, useLookupTicket, manualReasonText } from './slowLookup';

const CONSENT_TEXT =
  "I agree that Amber Enterprises may fetch my business's public GST, MCA and Udyam records to pre-fill this form. Only the fields shown will be stored.";

const ERROR_HELP = {
  GSTIN_NOT_FOUND: 'Double-check the number on your GST certificate.',
  GSTIN_INACTIVE: 'Only active GST registrations can be onboarded. Contact your GST practitioner if this looks wrong.',
  KYC_UPSTREAM_UNAVAILABLE: 'The registry service did not respond. Try again in a moment.',
  KYC_ATTEMPTS_EXCEEDED: 'You have used all verification attempts for this session. Ask for a fresh invite link.',
  KYC_DISABLED: 'Verification is switched off right now. Please try again later.',
};

const Chip = ({ icon, children, tone = 'neutral' }) => {
  const tones = {
    neutral: 'border-border bg-muted/40 text-text-secondary',
    success: 'border-success/30 bg-success/5 text-success',
    warning: 'border-warning/40 bg-warning/10 text-warning',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${tones[tone]}`}>
      {icon && <Icon name={icon} size={12} />}
      {children}
    </span>
  );
};

/**
 * GSTIN verification for the Identity step.
 * idle → verifying → verified | error. The server is the source of truth; this component only
 * pre-screens the checksum so a typo never costs an API call.
 *
 * Two ways past a registry that cannot help (the server records which, and Amber verifies the
 * details before approval): the lookup failed for a reason that is not the vendor's (down, timed
 * out, unreachable), or it has been running for more than 1.5 s — then "Enter details manually"
 * keeps the GSTIN as typed and nothing is pre-filled. A verified (or kept) GSTIN can be changed:
 * "Change GSTIN" unlocks and empties what the registry filled so the number can be entered again.
 */
const GstinVerifyPanel = ({ sessionId, identity, kyc, onVerified, disabled = false }) => {
  const verified = Boolean(identity?.verified_at);
  // The registry could not help and the vendor went on with the GSTIN as typed; a later verification clears this.
  const manual = !verified && Boolean(identity?.gstin_manual);
  const [gstin, setGstin] = useState(identity?.gstin || '');
  const [consent, setConsent] = useState(false);
  const [phase, setPhase] = useState(verified ? 'verified' : 'idle'); // idle | verifying | verified
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [shake, setShake] = useState(false);
  // The server said the registry did not answer (timeout, outage), so the vendor may continue by hand.
  const [manualAllowed, setManualAllowed] = useState(false);
  const [continuing, setContinuing] = useState(false);
  // "Try verifying again" from the kept-as-entered card shows the form once more.
  const [retrying, setRetrying] = useState(false);
  // "Change GSTIN" asks once before it empties the fetched details.
  const [confirmChange, setConfirmChange] = useState(false);
  const [changing, setChanging] = useState(false);
  const lookup = useLookupTicket();
  const busy = phase === 'verifying';
  const slow = useSlowFlag(busy);

  useEffect(() => {
    if (verified) {
      setPhase('verified');
      setGstin(identity.gstin || '');
    } else {
      setPhase((p) => (p === 'verifying' ? p : 'idle'));
    }
  }, [verified, identity?.gstin]);

  const normalized = normalizeGstin(gstin);
  const localProblem = gstinProblem(normalized);
  const ready = normalized.length === 15 && !localProblem && consent && !disabled && Boolean(sessionId);
  const enabled = kyc?.enabled !== false;
  const missing = 15 - normalized.length;
  // Why "Verify & fetch" is not clickable yet, in the vendor's words — a silent disabled button
  // reads as broken.
  const blockedBy = ready
    ? null
    : !sessionId
      ? 'Your session is still loading'
      : missing > 0
        ? `Enter all 15 characters — ${missing} more to go`
        : localProblem
          ? 'Fix the GSTIN first'
          : !consent
            ? 'Tick the consent box below to enable Verify & fetch'
            : null;

  const verify = async () => {
    if (!ready) return;
    const ticket = lookup.start();
    setPhase('verifying');
    setError(null);
    setErrorCode(null);
    setManualAllowed(false);
    try {
      const res = await registrationService.verifyGstin(sessionId, normalized);
      if (!lookup.isCurrent(ticket)) return; // the vendor went on by hand meanwhile
      if (res?.success && res.data) {
        setAttemptsRemaining(res.data.attempts_remaining ?? null);
        setRetrying(false);
        setPhase('verified');
        onVerified?.(res.data);
      } else {
        throw new Error(res?.message || 'Verification failed');
      }
    } catch (e) {
      if (!lookup.isCurrent(ticket)) return;
      const code = getErrorCode(e);
      setErrorCode(code);
      setError(getErrorMessage(e));
      setPhase('idle');
      setShake(true);
      setTimeout(() => setShake(false), 400);
      const details = getErrorDetails(e);
      if (details?.attempts_remaining?.[0] !== undefined) setAttemptsRemaining(Number(details.attempts_remaining[0]));
      setManualAllowed(details?.manual_allowed?.[0] === 'true');
    }
  };

  /** Keep the GSTIN as typed — after the registry failed, or while it is slow. */
  const continueManually = async () => {
    if (continuing || normalized.length !== 15 || localProblem) return;
    setContinuing(true);
    const walkingAway = busy;
    try {
      const res = await registrationService.gstinManual(sessionId, normalized);
      if (res?.success && res.data) {
        if (walkingAway) lookup.abandon(); // the running lookup's answer no longer matters
        setError(null);
        setErrorCode(null);
        setManualAllowed(false);
        setRetrying(false);
        setPhase('idle');
        onVerified?.(res.data);
      } else {
        throw new Error(res?.message || 'Could not continue');
      }
    } catch (e) {
      setErrorCode(getErrorCode(e));
      setError(getErrorMessage(e));
    } finally {
      setContinuing(false);
    }
  };

  /** "Change GSTIN": undo the verification (or the kept GSTIN) on the server and show the box again. */
  const changeGstin = async () => {
    setChanging(true);
    try {
      const res = await registrationService.clearGstin(sessionId);
      setConfirmChange(false);
      setRetrying(false);
      setConsent(false);
      setError(null);
      setErrorCode(null);
      setManualAllowed(false);
      setPhase('idle');
      onVerified?.(res?.data);
    } catch (e) {
      setError(getErrorMessage(e));
      setErrorCode(getErrorCode(e));
    } finally {
      setChanging(false);
    }
  };

  const changeControl = (tone) =>
    confirmChange ? (
      <div className="mt-3 rounded-md border border-border bg-white px-3 py-2" data-testid="gstin-change-confirm">
        <p className="text-xs text-foreground">
          This removes the details {tone === 'manual' ? 'kept for this GSTIN' : 'fetched from the GST registry'} so you can enter a different number. Continue?
        </p>
        <div className="mt-2 flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setConfirmChange(false)} disabled={changing}>
            Keep it
          </Button>
          <Button variant="destructive" size="sm" iconName="X" iconPosition="left" onClick={changeGstin} loading={changing} disabled={changing}>
            Yes, change GSTIN
          </Button>
        </div>
      </div>
    ) : null;

  const changeButton = (
    <button
      type="button"
      onClick={() => setConfirmChange(true)}
      aria-label="Change GSTIN"
      title="Change GSTIN"
      data-testid="gstin-change"
      className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-2.5 py-1 text-xs font-medium text-text-secondary hover:border-destructive/40 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <Icon name="X" size={12} /> Change GSTIN
    </button>
  );

  // ---------- kept as entered (the registry could not help) ----------
  if (manual && !retrying && phase !== 'verifying') {
    return (
      <div className="rounded-xl border border-warning/40 bg-warning/5 p-4 animate-rise-in motion-reduce:animate-none" role="status" aria-live="polite" data-testid="gstin-manual-card">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-warning/15 text-warning">
            <Icon name="ShieldAlert" size={20} strokeWidth={2.5} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-warning">GSTIN kept as entered — not verified yet</p>
              {changeButton}
            </div>
            <p className="mt-0.5 font-mono text-lg font-semibold tracking-[0.12em] text-foreground">{identity.gstin}</p>
            <p className="mt-1.5 text-sm text-text-secondary">
              You continued because {manualReasonText(identity.gstin_manual?.reason)}, so nothing was pre-filled: fill the rest of the form yourself.
              Amber will verify these details against the registry and your documents before approval.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Chip icon="Hash" tone="warning">PAN {identity.pan || panFromGstin(identity.gstin || '')}</Chip>
              <Button
                variant="outline"
                size="sm"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={() => {
                  setRetrying(true);
                  setGstin(identity.gstin || gstin);
                  setError(null);
                  setErrorCode(null);
                }}
              >
                Try verifying again
              </Button>
            </div>
            {changeControl('manual')}
            {error && <p className="mt-2 flex items-center gap-2 text-sm text-destructive" role="alert"><Icon name="AlertCircle" size={16} /> {error}</p>}
          </div>
        </div>
      </div>
    );
  }

  // ---------- verified ----------
  if (phase === 'verified' && identity) {
    const gst = identity.gst || {};
    const regYear = gst.registration_date ? gst.registration_date.slice(0, 4) : null;
    return (
      <div
        className="rounded-xl border border-success/30 bg-success/5 p-4 animate-rise-in motion-reduce:animate-none"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-success text-success-foreground animate-otp-pop motion-reduce:animate-none">
            <Icon name="ShieldCheck" size={20} strokeWidth={2.5} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-success">GSTIN verified</p>
              {changeButton}
            </div>
            <p className="mt-0.5 text-lg font-semibold leading-tight text-foreground [text-wrap:balance]">
              {preferTradeName(gst.trade_name, gst.legal_name) || 'Business found'}
            </p>
            {distinctLegalName(preferTradeName(gst.trade_name, gst.legal_name), gst.legal_name) && (
              <p className="text-sm text-text-secondary">Legal name: {gst.legal_name}</p>
            )}
            <p className="mt-1 font-mono text-sm text-text-secondary">{identity.gstin}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {gst.constitution && <Chip icon="Building2">{gst.constitution}</Chip>}
              <Chip icon="BadgeCheck" tone="success">
                {gst.status || 'Active'}
              </Chip>
              {identity.pan && <Chip icon="Fingerprint">PAN {identity.pan} derived</Chip>}
              {regYear && <Chip icon="CalendarCheck">GST since {regYear}</Chip>}
              {identity.mca_status === 'done' && identity.mca?.cin && (
                <Chip icon="Landmark" tone="success">
                  CIN {identity.mca.cin}
                </Chip>
              )}
              {identity.mca_status === 'failed' && (
                <Chip icon="AlertTriangle" tone="warning">
                  MCA lookup unavailable — add CIN later
                </Chip>
              )}
              {identity.msme_hint?.exists && (
                <Chip icon="Factory" tone="success">
                  Udyam registration found
                </Chip>
              )}
              {gst.filing_current === true && (
                <Chip icon="FileCheck" tone="success">
                  GST returns filed in {gst.filing_year}
                </Chip>
              )}
            </div>
            {/* Nothing filed this year: said in red, in the registry's own terms. It never blocks. */}
            {gst.filing_warning && (
              <div
                role="alert"
                className="mt-3 flex items-start gap-2 rounded-md border-2 border-destructive/70 bg-destructive/10 px-3 py-2.5 text-sm font-medium text-destructive"
              >
                <Icon name="AlertTriangle" size={18} strokeWidth={2.25} className="mt-0.5 shrink-0" />
                <span>{gst.filing_warning}</span>
              </div>
            )}
            {changeControl('verified')}
            {error && <p className="mt-2 flex items-center gap-2 text-sm text-destructive" role="alert"><Icon name="AlertCircle" size={16} /> {error}</p>}
          </div>
        </div>
      </div>
    );
  }

  // ---------- disabled by ops ----------
  if (!enabled) {
    return (
      <div className="rounded-xl border border-warning/40 bg-warning/10 p-4" role="status">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="mt-0.5 flex-shrink-0 text-warning" />
          <div>
            <p className="text-sm font-semibold text-foreground">GSTIN verification is temporarily unavailable</p>
            <p className="text-sm text-text-secondary">
              Your draft is saved for 7 days. Please come back a little later to continue.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---------- idle / verifying ----------
  return (
    <div
      className={`rounded-xl border border-primary/30 bg-white p-4 shadow-sm animate-rise-in motion-reduce:animate-none ${
        shake ? 'animate-otp-shake' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Icon name="ScanSearch" size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Verify your GSTIN to pre-fill the form</p>
          <p className="text-xs text-text-secondary">
            One lookup fills your company name, address, PAN, constitution and more — straight from GSTN.
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto] md:items-start">
        <div className="space-y-2">
          <label htmlFor="gstin-input" className="text-sm font-medium leading-none text-foreground">
            GSTIN <span className="ml-1 text-destructive">*</span>
          </label>
          <div className="relative">
            <input
              id="gstin-input"
              value={gstin}
              onChange={(e) => {
                setGstin(normalizeGstin(e.target.value).slice(0, 15));
                setError(null);
                setManualAllowed(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') verify();
              }}
              placeholder="27AAACR5055K1Z7"
              maxLength={15}
              autoComplete="off"
              spellCheck={false}
              disabled={busy || disabled}
              aria-invalid={Boolean(localProblem || error)}
              aria-describedby="gstin-help"
              className={`flex h-11 w-full rounded-md border bg-white px-3 pr-10 font-mono text-base tracking-[0.12em] text-foreground shadow-sm placeholder:tracking-normal placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60 ${
                localProblem || error ? 'border-destructive' : normalized.length === 15 ? 'border-success/50' : 'border-border'
              }`}
            />
            <ClearInputButton
              visible={Boolean(gstin) && !busy && !disabled}
              label="Clear GSTIN"
              testId="gstin-clear-input"
              onClear={() => {
                setGstin('');
                setError(null);
                setErrorCode(null);
                setManualAllowed(false);
                document.getElementById('gstin-input')?.focus();
              }}
            />
          </div>
          <p id="gstin-help" className={`text-xs ${localProblem ? 'text-destructive' : 'text-text-secondary'}`}>
            {localProblem ? (
              localProblem
            ) : normalized.length === 15 ? (
              <>
                Checksum OK · PAN will be <span className="font-mono">{panFromGstin(normalized)}</span>
              </>
            ) : normalized.length === 0 ? (
              '15 characters: your 2-digit state code, your 10-character PAN, an entity number, the letter Z and a check character.'
            ) : (
              `${normalized.length} of 15 characters — ${missing} more to go. The last character is a check digit; copy it from your GST certificate.`
            )}
          </p>
        </div>
        <Button
          variant="default"
          onClick={verify}
          loading={busy}
          disabled={!ready || busy}
          title={!busy && blockedBy ? blockedBy : undefined}
          iconName="ShieldCheck"
          iconPosition="right"
          className="h-11 md:mt-[26px]"
        >
          Verify & fetch
        </Button>
      </div>
      {!busy && blockedBy && normalized.length === 15 && !localProblem && (
        <p className="mt-2 text-xs text-text-secondary" role="status">{blockedBy}.</p>
      )}

      {/* The registry is slow: the vendor may stop waiting and go on with the GSTIN as typed. */}
      {busy && slow && (
        <div className="mt-3 rounded-md border border-warning/40 bg-warning/5 px-3 py-2.5" role="status" data-testid="gstin-slow-offer">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Icon name="Hourglass" size={16} className="text-warning" /> The GST registry is taking longer than usual.
          </p>
          <p className="mt-1 text-xs text-text-secondary">
            Keep waiting, or continue with this GSTIN and fill in your company details yourself — Amber verifies them before approval.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            iconName="PencilLine"
            iconPosition="left"
            loading={continuing}
            disabled={continuing}
            onClick={continueManually}
          >
            Enter details manually
          </Button>
        </div>
      )}

      <div className="mt-3">
        <Checkbox
          id="gstin-consent"
          checked={consent}
          onCheckedChange={(v) => setConsent(Boolean(v))}
          disabled={busy}
          label={CONSENT_TEXT}
          size="sm"
        />
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2" role="alert">
          <p className="flex items-center gap-2 text-sm font-medium text-destructive">
            <Icon name="AlertCircle" size={16} />
            {error}
          </p>
          {ERROR_HELP[errorCode] && !manualAllowed && <p className="mt-1 text-xs text-text-secondary">{ERROR_HELP[errorCode]}</p>}
          {manualAllowed && (
            <div className="mt-2 rounded-md border border-warning/40 bg-white px-3 py-2" data-testid="gstin-manual-offer">
              <p className="text-xs text-text-secondary">
                You can continue with this GSTIN and enter your details yourself. Nothing will be pre-filled, and Amber will verify everything
                against the registry and your documents before approval.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                iconName="PencilLine"
                iconPosition="left"
                loading={continuing}
                disabled={continuing || normalized.length !== 15 || Boolean(localProblem)}
                onClick={continueManually}
              >
                Enter details manually
              </Button>
            </div>
          )}
          {attemptsRemaining !== null && errorCode !== 'KYC_ATTEMPTS_EXCEEDED' && (
            <p className="mt-1 text-xs text-text-secondary">
              {attemptsRemaining} attempt{attemptsRemaining === 1 ? '' : 's'} remaining
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GstinVerifyPanel;
